import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Mail } from 'lucide-react';

const STATUS_TONE = {
    sent: 'text-[#0B8A3E] border-[#0B8A3E]/30',
    sent_in_digest: 'text-[#0B8A3E] border-[#0B8A3E]/30',
    failed: 'text-[#C3341C] border-[#C3341C]/30',
    skipped_no_key: 'text-[#B87A2C] border-[#B87A2C]/30',
    digest_queued: 'text-[#0B1F36] border-[#0B1F36]/25',
    pending: 'text-neutral-500 border-neutral-300',
};

const STATUS_LABEL = {
    skipped_no_key: 'not sent — email inactive',
    sent_in_digest: 'in digest',
    digest_queued: 'queued for digest',
};

const FOLLOWUP_TONE = {
    new: 'bg-white border-neutral-300 text-neutral-600',
    contacted: 'bg-[#0B1F36] border-[#0B1F36] text-white',
    booked: 'bg-brand-gold border-brand-gold text-brand-navy',
    closed: 'bg-neutral-200 border-neutral-200 text-neutral-500',
};

const TIER_TONE = {
    hot: 'bg-[#C3341C] text-white',
    warm: 'bg-brand-gold text-brand-navy',
    cool: 'bg-neutral-200 text-neutral-600',
};

export default function AdminSubmissions() {
    const [formType, setFormType] = useState('');
    const [status, setStatus] = useState('');
    const [tier, setTier] = useState('');
    const [followup, setFollowup] = useState('');
    const [staleOnly, setStaleOnly] = useState(false);
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(null);

    const { data, refetch, isLoading } = useQuery({
        queryKey: ['admin-subs', formType, status, tier, followup, staleOnly, q],
        queryFn: async () => (await api.get('/admin/form-submissions', { params: { form_type: formType, status, tier, followup, stale: staleOnly ? 'true' : '', q } })).data,
    });

    const { data: notif, refetch: refetchNotif } = useQuery({
        queryKey: ['admin-notif-settings'],
        queryFn: async () => (await api.get('/admin/notifications/settings')).data,
    });

    const setMode = async (mode) => {
        try {
            await api.post('/admin/notifications/settings', { mode });
            refetchNotif();
            toast.success(mode === 'digest' ? 'Daily digest on — one email at 7am ET, hot leads first' : 'Instant notifications on — one email per lead');
        } catch (e) { toast.error('Could not update notification mode'); }
    };

    const sendDigestNow = async () => {
        try {
            const { data: r } = await api.post('/admin/notifications/send-digest-now');
            r.sent ? toast.success(`Digest sent — ${r.count} leads`) : toast.info(r.reason === 'no_new_leads' ? 'No new leads in the last 24 hours' : (r.error || `Status: ${r.status}`));
        } catch (e) { toast.error(e?.response?.data?.detail || 'Digest failed'); }
    };

    const setFollowupStatus = async (id, s) => {
        try {
            await api.patch(`/admin/form-submissions/${id}/status`, { status: s });
            toast.success(`Marked ${s}`);
            refetch();
        } catch (e) { toast.error('Could not update status'); }
    };

    const retry = async (id) => {
        try {
            const { data: r } = await api.post(`/admin/form-submissions/${id}/retry-email`);
            r.ok ? toast.success('Notification re-sent') : toast.error(r.error || `Status: ${r.status}`);
            refetch();
        } catch (e) {
            toast.error('Retry failed');
        }
    };

    const items = data?.items || [];
    const types = data?.form_types || {};

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14" data-testid="admin-submissions">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Admin</div>
                    <h1 className="font-editorial text-[40px] tracking-editorial mt-2">Form submissions</h1>
                </div>
                <div className="flex gap-4 text-[13px]">
                    <Link to="/admin/leads" className="link-editorial">Leads</Link>
                    <Link to="/admin/newsletter" className="link-editorial">Newsletter</Link>
                    <Link to="/admin/videos" className="link-editorial">Videos</Link>
                </div>
            </div>

            {data && !data.email_configured && (
                <div className="mt-6 border-l-2 border-[#B87A2C] bg-[#B87A2C]/5 pl-4 py-3 text-[13.5px] text-neutral-700" data-testid="email-not-configured-banner">
                    <strong>Email notifications inactive.</strong> Every submission is being saved below — no emails
                    are sent or queued for automatic sending. When Resend is activated, only <em>new</em> submissions
                    will trigger notifications; older ones stay put unless you press "Retry notification" on them individually.
                </div>
            )}

            <div className="mt-6 border border-neutral-200 rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="notif-mode-card">
                <div>
                    <div className="font-editorial text-[18px] tracking-editorial">Notification mode</div>
                    <p className="mt-1 text-[13px] text-neutral-600 max-w-xl">
                        {notif?.mode === 'digest'
                            ? `One morning email at ${notif?.digest_hour_et ?? 7}:00am ET listing the last 24 hours of leads, hottest first — nothing per-lead. ${notif?.last_digest_result ? `Last digest: ${notif.last_digest_result}.` : 'Only sends when there are new leads.'}`
                            : 'One email per lead, the moment it arrives. Switch to the daily digest for a single 7am ET summary instead.'}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="flex border border-neutral-300 rounded-full overflow-hidden text-[12.5px]" role="radiogroup" aria-label="Notification mode">
                        <button onClick={() => setMode('instant')} aria-checked={notif?.mode !== 'digest'} role="radio"
                            className={`px-4 py-2 transition-colors ${notif?.mode !== 'digest' ? 'bg-brand-navy text-white' : 'text-neutral-600 hover:text-brand-navy'}`}
                            data-testid="notif-mode-instant">Instant</button>
                        <button onClick={() => setMode('digest')} aria-checked={notif?.mode === 'digest'} role="radio"
                            className={`px-4 py-2 border-l border-neutral-300 transition-colors ${notif?.mode === 'digest' ? 'bg-brand-navy text-white' : 'text-neutral-600 hover:text-brand-navy'}`}
                            data-testid="notif-mode-digest">Daily digest</button>
                    </div>
                    <button onClick={sendDigestNow} className="text-[12.5px] text-brand-navy underline underline-offset-4 hover:text-brand-gold px-1" data-testid="notif-send-digest-now">
                        Send digest now
                    </button>
                </div>
            </div>

            {data?.stale_count > 0 && (
                <button
                    onClick={() => setStaleOnly(!staleOnly)}
                    aria-pressed={staleOnly}
                    className={`mt-6 w-full text-left border rounded-lg px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                        staleOnly ? 'border-[#C3341C] bg-[#C3341C]/10' : 'border-[#C3341C]/40 bg-[#C3341C]/5 hover:bg-[#C3341C]/10'
                    }`}
                    data-testid="stale-alert-banner"
                >
                    <span className="text-[13.5px] text-neutral-800">
                        <span className="inline-block w-2 h-2 rounded-full bg-[#C3341C] mr-2 align-middle animate-pulse" />
                        <strong>{data.stale_count} hot lead{data.stale_count !== 1 ? 's' : ''}</strong> still marked new after {data.stale_hours}+ hours — call before they cool off.
                    </span>
                    <span className="shrink-0 text-[12px] text-[#C3341C] underline underline-offset-2">
                        {staleOnly ? 'Show all' : 'Show them'}
                    </span>
                </button>
            )}

            {data?.followup_counts && (
                <div className="mt-6 flex flex-wrap gap-2" data-testid="followup-pipeline">
                    {(data.follow_up_statuses || []).map((s) => (
                        <button key={s} onClick={() => setFollowup(followup === s ? '' : s)}
                            className={`text-[12px] uppercase tracking-[0.1em] font-mono-data border rounded-full px-3.5 py-1.5 transition-colors ${followup === s ? 'bg-brand-navy border-brand-navy text-white' : 'border-neutral-300 text-neutral-600 hover:border-brand-navy'}`}
                            data-testid={`followup-filter-${s}`}>
                            {s} · {data.followup_counts[s] || 0}
                        </button>
                    ))}
                </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
                <select value={formType} onChange={(e) => setFormType(e.target.value)} className="bn-input text-[13px]" data-testid="subs-filter-type" aria-label="Filter by form type">
                    <option value="">All form types</option>
                    {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v} ({data?.counts?.[k] || 0})</option>)}
                </select>
                <select value={tier} onChange={(e) => setTier(e.target.value)} className="bn-input text-[13px]" data-testid="subs-filter-tier" aria-label="Filter by lead temperature">
                    <option value="">Any temperature</option>
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cool">Cool</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="bn-input text-[13px]" data-testid="subs-filter-status" aria-label="Filter by delivery status">
                    <option value="">Any delivery status</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="skipped_no_key">Skipped (no key)</option>
                    <option value="pending">Pending</option>
                </select>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, market…" className="bn-input text-[13px] flex-1 min-w-[220px]" data-testid="subs-search" aria-label="Search submissions" />
            </div>

            <div className="mt-6 border-y border-neutral-200 divide-y divide-neutral-200">
                {isLoading && <div className="py-10 text-neutral-500 text-[14px]">Loading…</div>}
                {!isLoading && items.length === 0 && (
                    <div className="py-12 text-neutral-500 text-[14px]" data-testid="subs-empty">No submissions match these filters yet.</div>
                )}
                {items.map((s) => (
                    <div key={s.submission_id} className="py-4">
                        <button onClick={() => setOpen(open === s.submission_id ? null : s.submission_id)} className="w-full flex items-center justify-between gap-4 text-left" data-testid={`sub-row-${s.submission_id}`}>
                            <div className="min-w-0">
                                <div className="font-editorial text-[17px] tracking-editorial truncate flex items-center gap-2">
                                    {s.lead_tier && (
                                        <span className={`shrink-0 text-[9.5px] uppercase tracking-[0.14em] font-mono-data rounded-full px-2 py-0.5 ${TIER_TONE[s.lead_tier] || TIER_TONE.cool}`} data-testid={`sub-tier-${s.submission_id}`}>
                                            {s.lead_tier}
                                        </span>
                                    )}
                                    <span className="truncate">{s.fields?.name || s.fields?.email || '—'} <span className="text-neutral-400">· {s.form_label}</span></span>
                                </div>
                                <div className="text-[12px] text-neutral-500 font-mono-data mt-0.5">
                                    {s.fields?.email} · {new Date(s.created_at).toLocaleString('en-CA')} · {s.source_page || '—'}
                                </div>
                            </div>
                            <div className="shrink-0 flex items-center gap-2">
                                {s.is_stale && (
                                    <span className="text-[10.5px] uppercase tracking-[0.14em] font-mono-data rounded-full px-2.5 py-1 bg-[#C3341C] text-white" data-testid={`stale-flag-${s.submission_id}`}>
                                        waiting {s.hours_waiting}h
                                    </span>
                                )}
                                {s.follow_up_status && s.follow_up_status !== 'new' && (
                                    <span className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data border rounded-full px-2.5 py-1 ${FOLLOWUP_TONE[s.follow_up_status] || FOLLOWUP_TONE.new}`}>
                                        {s.follow_up_status}
                                    </span>
                                )}
                                <span className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data border rounded-md px-2 py-1 ${STATUS_TONE[s.email_status] || STATUS_TONE.pending}`}>
                                    {STATUS_LABEL[s.email_status] || s.email_status}
                                </span>
                            </div>
                        </button>
                        {open === s.submission_id && (
                            <div className="mt-4 bg-[#F7F7F5] rounded-lg p-5 text-[13.5px]">
                                <div className="flex items-center gap-2 flex-wrap mb-4" data-testid={`followup-actions-${s.submission_id}`}>
                                    <span className="text-[10.5px] uppercase tracking-[0.12em] font-mono-data text-neutral-500 mr-1">Follow-up:</span>
                                    {(data?.follow_up_statuses || ['new', 'contacted', 'booked', 'closed']).map((st) => (
                                        <button key={st} onClick={() => setFollowupStatus(s.submission_id, st)}
                                            aria-pressed={(s.follow_up_status || 'new') === st}
                                            className={`text-[11.5px] uppercase tracking-[0.1em] font-mono-data border rounded-full px-3 py-1.5 transition-colors ${
                                                (s.follow_up_status || 'new') === st
                                                    ? (FOLLOWUP_TONE[st] || FOLLOWUP_TONE.new) + ' ring-1 ring-brand-navy/30'
                                                    : 'bg-white border-neutral-300 text-neutral-500 hover:border-brand-navy hover:text-brand-navy'
                                            }`}
                                            data-testid={`followup-set-${st}-${s.submission_id}`}>
                                            {st}
                                        </button>
                                    ))}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1.5">
                                    {Object.entries(s.fields || {}).map(([k, v]) => (
                                        <div key={k} className="flex gap-2">
                                            <span className="text-neutral-500 uppercase text-[10.5px] tracking-[0.12em] font-mono-data mt-1 w-28 shrink-0">{k}</span>
                                            <span className="text-neutral-800 break-words">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 flex items-center gap-4 text-[12px] text-neutral-500 font-mono-data flex-wrap">
                                    <span>{s.submission_id}</span>
                                    {s.provider_id && <span>provider: {s.provider_id}</span>}
                                    {s.email_error && <span className="text-[#C3341C]">error: {s.email_error}</span>}
                                    {s.confirm_status && <span>visitor confirmation: {s.confirm_status}</span>}
                                    {s.email_status !== 'sent' && (
                                        <button onClick={() => retry(s.submission_id)} className="inline-flex items-center gap-1.5 text-brand-navy underline underline-offset-2" data-testid={`sub-retry-${s.submission_id}`}>
                                            <RefreshCw className="w-3 h-3" /> Retry notification
                                        </button>
                                    )}
                                    {s.fields?.email && (
                                        <a href={`mailto:${s.fields.email}`} className="inline-flex items-center gap-1.5 text-brand-navy underline underline-offset-2">
                                            <Mail className="w-3 h-3" /> Reply
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
